import { C, S } from "../customers/theme";
import { PO_STATUSES } from "../lib/purchaseOrders";
import { todayISO } from "../lib/dates";

const statusOf = (v) => PO_STATUSES.find((s) => s.value === v) || { label: v, color: C.textDim };

export function POStatusBadge({ status }) {
  const s = statusOf(status);
  return <span style={S.pill(s.color)}>{s.label}</span>;
}

export default function PurchaseOrderView({ po, onClose, onEdit }) {
  const cur = po.preferred_currency === "GBP" ? "£" : po.preferred_currency === "USD" ? "$" : "€";
  const money = (n) => `${cur}${(Number(n) || 0).toFixed(2)}`;
  const lines = (po.purchase_order_lines || []).slice().sort((a, b) => (a.position || 0) - (b.position || 0));
  const sup = po.suppliers || {};
  const overdue = po.expected_delivery_date && po.expected_delivery_date < todayISO() && !["received", "cancelled"].includes(po.status);

  return (
    <div style={S.modal} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={{ ...S.modalBox, width: 820 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>{po.po_number}</h2>
            <div style={{ color: C.textMuted, fontSize: 13, marginTop: 3 }}>{sup.company_name || "—"}</div>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <POStatusBadge status={po.status} />
            <button onClick={() => onEdit(po)} style={S.btn()}>Edit</button>
            <button onClick={onClose} aria-label="Close" style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: C.textMuted }}>✕</button>
          </div>
        </div>

        {overdue && (
          <div role="alert" style={{ margin: "10px 0", background: C.warningDim, border: `1px solid ${C.warning}`, color: C.text, borderRadius: 8, padding: "8px 12px", fontSize: 12, fontWeight: 600 }}>
            ⚠ Overdue — expected {po.expected_delivery_date}, not yet received.
          </div>
        )}

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18, marginTop: 14 }}>
          <div>
            <div style={S.sectionTitle}>Supplier</div>
            <div style={{ fontSize: 13, lineHeight: 1.7 }}>
              <div><strong>{sup.company_name || "—"}</strong></div>
              {po.supplier_contact && <div style={{ color: C.textMuted }}>👤 {po.supplier_contact}</div>}
              {po.supplier_email && <div style={{ color: C.textMuted }}>✉️ {po.supplier_email}</div>}
              {sup.phone && <div style={{ color: C.textMuted }}>☎️ {sup.phone}</div>}
            </div>
          </div>
          <div>
            <div style={S.sectionTitle}>Order</div>
            <div style={{ fontSize: 13, lineHeight: 1.7, color: C.textMuted }}>
              <div>Order date: <span style={{ color: C.text }}>{po.order_date || "—"}</span></div>
              <div>Expected: <span style={{ color: C.text }}>{po.expected_delivery_date || "—"}</span></div>
              <div>Payment terms: <span style={{ color: C.text }}>{(po.payment_terms || "—").replace(/_/g, " ")}</span></div>
              <div>Currency: <span style={{ color: C.text }}>{po.preferred_currency}</span></div>
            </div>
          </div>
        </div>

        <div style={S.sectionTitle}>Line Items</div>
        <table style={S.table}>
          <thead><tr>
            <th style={S.th}>Description</th><th style={S.th}>SKU</th><th style={S.th}>Qty</th>
            <th style={S.th}>Unit Cost</th><th style={S.th}>Disc%</th><th style={S.th}>VAT%</th><th style={S.th}>Line Total</th>
          </tr></thead>
          <tbody>
            {lines.length === 0 ? <tr><td colSpan={7} style={S.empty}>No line items</td></tr> : lines.map((l) => (
              <tr key={l.id}>
                <td style={S.td}>{l.description || "—"}</td>
                <td style={S.td}>{l.sku || "—"}</td>
                <td style={S.td}>{l.quantity}</td>
                <td style={S.td}>{money(l.unit_cost)}</td>
                <td style={S.td}>{l.discount_percent || 0}%</td>
                <td style={S.td}>{l.vat_rate}%</td>
                <td style={S.td}><strong>{money(l.line_total)}</strong></td>
              </tr>
            ))}
          </tbody>
        </table>

        <div style={{ background: C.surface2, borderRadius: 8, padding: 14, margin: "14px 0", maxWidth: 320, marginLeft: "auto" }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}><span style={{ color: C.textMuted }}>Subtotal</span><strong>{money(po.subtotal)}</strong></div>
          {po.total_discount > 0 && <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}><span style={{ color: C.textMuted }}>Discount</span><strong>−{money(po.total_discount)}</strong></div>}
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}><span style={{ color: C.textMuted }}>VAT</span><strong>{money(po.total_vat)}</strong></div>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 16, fontWeight: 800, borderTop: `1px solid ${C.border}`, paddingTop: 8 }}><span>Total</span><span>{money(po.total_amount)}</span></div>
        </div>

        {po.notes && (<><div style={S.sectionTitle}>Notes</div><div style={{ fontSize: 13, whiteSpace: "pre-wrap" }}>{po.notes}</div></>)}
      </div>
    </div>
  );
}
